import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, EnvelopeSimple, WhatsappLogo, InstagramLogo, FacebookLogo, TiktokLogo } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";

const INIT = { name: "", email: "", subject: "", message: "" };

const CHANNELS = [
  { icon: EnvelopeSimple, label: "support@islandhoptt.com", sub: "General support", href: "mailto:support@islandhoptt.com" },
  { icon: EnvelopeSimple, label: "partners@islandhoptt.com", sub: "Merchant partnerships", href: "mailto:partners@islandhoptt.com" },
  { icon: EnvelopeSimple, label: "drivers@islandhoptt.com", sub: "Driver onboarding", href: "mailto:drivers@islandhoptt.com" },
  { icon: WhatsappLogo, label: "WhatsApp", sub: "Mon–Sat · 9am–7pm", href: "https://wa.me/0000000000" },
];

export default function Contact() {
  useSeo({
    title: "Contact",
    description: "Reach IslandHop for support, partnerships, press or investor inquiries.",
  });
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message);
      setSubmitted(true);
      setForm(INIT);
    } catch {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative pt-20 lg:pt-28 pb-12 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-40" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] ih-radial-orange" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 space-y-6 max-w-3xl">
          <SectionEyebrow label="Contact" color="orange" />
          <h1 className="font-display font-black text-5xl lg:text-7xl text-white leading-[0.95] text-balance">
            Let&apos;s talk.<br />
            <span className="text-ih-orange">We&apos;re listening.</span>
          </h1>
          <p className="text-white/70 text-lg max-w-xl leading-relaxed">
            Press, partnerships, support, investor inquiries — drop us a line and the right person will get back to you.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-3">
            {CHANNELS.map((c, i) => {
              const Icon = c.icon;
              return (
                <a
                  key={i}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={`contact-channel-${i}`}
                  className="flex items-center gap-4 bg-ih-surface border border-ih-line rounded-2xl p-5 hover:border-ih-orange/40 transition"
                >
                  <span className="w-11 h-11 rounded-xl bg-black/40 border border-ih-line2 grid place-items-center text-ih-orange">
                    <Icon weight="duotone" size={22} />
                  </span>
                  <div>
                    <p className="text-white font-semibold">{c.label}</p>
                    <p className="text-xs text-white/50">{c.sub}</p>
                  </div>
                </a>
              );
            })}

            <div className="bg-ih-surface border border-ih-line rounded-2xl p-5">
              <p className="text-xs uppercase tracking-[0.16em] text-white/40 mb-3">Follow</p>
              <div className="flex gap-3">
                <Social Icon={InstagramLogo} label="Instagram" />
                <Social Icon={FacebookLogo} label="Facebook" />
                <Social Icon={TiktokLogo} label="TikTok" />
                <Social Icon={WhatsappLogo} label="WhatsApp" />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <Success onReset={() => setSubmitted(false)} />
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-ih-surface border border-ih-line rounded-3xl p-8 lg:p-10 space-y-5"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Your name *">
                    <input required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} data-testid="contact-name" />
                  </Field>
                  <Field label="Email *">
                    <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="contact-email" />
                  </Field>
                </div>
                <Field label="Subject">
                  <input value={form.subject} onChange={(e) => update("subject", e.target.value)} className={inputCls} placeholder="What's this about?" data-testid="contact-subject" />
                </Field>
                <Field label="Message *">
                  <textarea required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className={`${inputCls} resize-none`} data-testid="contact-message" />
                </Field>

                <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-ih-orange text-black font-semibold hover:scale-[1.02] disabled:opacity-60 transition w-full sm:w-auto justify-center">
                  {loading ? "Sending..." : "Send message"}
                </button>
              </motion.form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-ih-line2 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-ih-orange transition text-sm";

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs uppercase tracking-[0.16em] text-white/50">{label}</span>
    {children}
  </label>
);

const Social = ({ Icon, label }) => (
  <a href="#" aria-label={label} className="w-10 h-10 rounded-full border border-ih-line2 text-white/70 hover:text-white hover:border-ih-orange flex items-center justify-center transition">
    <Icon size={18} weight="bold" />
  </a>
);

const Success = ({ onReset }) => (
  <div data-testid="contact-success" className="bg-ih-surface border border-ih-orange/40 rounded-3xl p-10 lg:p-14 text-center ih-shadow-orange">
    <CheckCircle weight="duotone" size={56} className="mx-auto text-ih-orange mb-5" />
    <h3 className="font-display font-extrabold text-3xl text-white mb-3">Message received</h3>
    <p className="text-white/65 max-w-md mx-auto leading-relaxed">
      Thanks for reaching out. The right person at IslandHop will get back to you shortly.
    </p>
    <button onClick={onReset} className="mt-6 text-sm text-ih-orange underline underline-offset-4">Send another</button>
  </div>
);
